import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ImageGallery.css';

const ImageGallery = ({ images = [], alt = "Property" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const galleryRef = useRef(null);

  // Filter out invalid images and ensure we have an array
  const validImages = Array.isArray(images) ? images.filter(img => img && typeof img === 'string') : [];
  
  // Reset index if images change
  useEffect(() => {
    setCurrentIndex(0);
  }, [images]);

  const goToNext = useCallback(() => {
    if (validImages.length <= 1) return;
    setCurrentIndex((prevIndex) => 
      prevIndex === validImages.length - 1 ? 0 : prevIndex + 1
    );
  }, [validImages.length]);

  const goToPrevious = useCallback(() => {
    if (validImages.length <= 1) return;
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? validImages.length - 1 : prevIndex - 1
    );
  }, [validImages.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (validImages.length <= 1) return;
      
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    const currentRef = galleryRef.current;
    if (currentRef) {
      currentRef.addEventListener('keydown', handleKeyPress);
      return () => {
        currentRef.removeEventListener('keydown', handleKeyPress);
      };
    }
  }, [validImages.length, currentIndex, goToPrevious, goToNext]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Touch handlers for swipe gestures
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && validImages.length > 1) {
      goToNext();
    }
    if (isRightSwipe && validImages.length > 1) {
      goToPrevious();
    }
  };

  // Handle image load error
  const handleImageError = (e) => {
    e.target.style.display = 'none';
    // Show placeholder if this was the only/last image
    const placeholder = e.target.nextElementSibling;
    if (placeholder && placeholder.classList.contains('gallery-placeholder')) {
      placeholder.style.display = 'flex';
    }
  };

  if (validImages.length === 0) {
    return (
      <div className="image-gallery">
        <div className="gallery-placeholder">
          🏠
        </div>
      </div>
    );
  }

  return (
    <div 
      className="image-gallery"
      ref={galleryRef}
      tabIndex={0}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="gallery-container">
        <img
          src={validImages[currentIndex]}
          alt={`${alt} ${currentIndex + 1}`}
          className="gallery-image"
          onError={handleImageError}
        />
        <div className="gallery-placeholder hidden">🏠</div>

        {/* Navigation arrows - only show if more than 1 image */}
        {validImages.length > 1 && (
          <>
            <button
              className="gallery-nav gallery-nav-prev"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              className="gallery-nav gallery-nav-next"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              aria-label="Next image"
            >
              ›
            </button>
          </>
        )}

        {/* Image counter */}
        {validImages.length > 1 && (
          <div className="gallery-counter">
            {currentIndex + 1} / {validImages.length}
          </div>
        )}

        {/* Dots indicator - only show if more than 1 image and less than 6 images */}
        {validImages.length > 1 && validImages.length <= 5 && (
          <div className="gallery-dots">
            {validImages.map((_, index) => (
              <button
                key={index}
                className={`gallery-dot ${index === currentIndex ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  goToSlide(index);
                }}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGallery;
